import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";
import Spinner from "react-bootstrap/Spinner";
import { db } from "../../../services/firebase/Config";
import { getPlanes } from "../../../services/firebase/planes/index";
import {
  MainContainer,
  ContainerTitle,
  ContainerTitleText,
  ContainerBody,
  FlexContainer,
  StyledSelect,
  StyledInput,
  StyledButton,
  GridResumen,
  CardResumen,
  FlexGridSection,
  SectionTitle,
  ProgressBarWrapper,
  ProgressBarLabel,
  ProgressBarContainer,
  ProgressBarFill,
  TemplateList,
  TemplateItem,
  Tabla,
  TablaHeader,
  TablaHeaderTexto,
  TablaBody,
  TablaBodyRow,
  TablaBodyTexto,
  PaginatorContainer,
  DetailFiltersContainer,
  ToggleButton
} from "./styles";

const getPlanLimits = (empresa, planesList = []) => {
  const dbMembresia = empresa.membresia || {};

  let planName = "FREE";
  if (dbMembresia.plan) {
    if (typeof dbMembresia.plan === "object") {
      planName = dbMembresia.plan.nombre || dbMembresia.plan.plan || "FREE";
    } else {
      planName = dbMembresia.plan;
    }
  } else if (empresa.tipoMembresia) {
    planName = empresa.tipoMembresia;
  }

  const dbPlan = planesList.find(
    (p) =>
      p.id.toLowerCase() === planName.toLowerCase() ||
      p.nombre?.toLowerCase() === planName.toLowerCase(),
  );

  const dbFreePlan =
    planesList.find(
      (p) =>
        p.id.toLowerCase() === "free" || p.nombre?.toLowerCase() === "free",
    ) || {};

  const limitSource =
    typeof dbMembresia.plan === "object" ? dbMembresia.plan : {};

  return {
    plan: planName,
    maxMensajesWhatsapp:
      dbMembresia.maxMensajesWhatsapp ||
      limitSource.maxMensajesWhatsapp ||
      dbPlan?.maxMensajesWhatsapp ||
      dbFreePlan.maxMensajesWhatsapp ||
      0,
  };
};

const convertTimestamp = (ts) => {
  if (!ts) return null;
  if (typeof ts.toDate === "function") return ts.toDate();
  return new Date(ts);
};

export const WhatsappRpt = () => {
  const empresa = useSelector((state) => state.empresa);

  const [loading, setLoading] = useState(true);
  const [queryLoading, setQueryLoading] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("current");

  // Custom Date range
  const getTodayStr = () => moment().format("YYYY-MM-DD");
  const [fecIni, setFecIni] = useState(getTodayStr());
  const [fecFin, setFecFin] = useState(getTodayStr());

  // Report results
  const [messages, setMessages] = useState([]);
  const [summary, setSummary] = useState({
    totalOutbound: 0,
    planLimit: 0,
    usagePercent: 0,
  });
  const [typeStats, setTypeStats] = useState({
    text: 0,
    template: 0,
    document: 0,
    image: 0,
    audio: 0,
    video: 0,
  });
  const [templateStats, setTemplateStats] = useState([]);

  // Advanced toggles & filters
  const [showDetails, setShowDetails] = useState(false);
  const [filterTemplate, setFilterTemplate] = useState("all");
  const [filterDay, setFilterDay] = useState("all");
  const [availableDays, setAvailableDays] = useState([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // 1. Load plans & billing periods
  useEffect(() => {
    async function loadInitialData() {
      if (!empresa || !empresa.id) return;
      setLoading(true);
      try {
        const [planesList, pagosSnapshot] = await Promise.all([
          getPlanes(),
          db
            .collection("empresas")
            .doc(empresa.id)
            .collection("pagos")
            .where("estado", "==", "confirmado")
            .get(),
        ]);

        setPlanes(planesList || []);

        // Build active period
        const ultPago =
          convertTimestamp(empresa.fecUltPago) ||
          moment().startOf("month").toDate();
        const vencimiento =
          convertTimestamp(empresa.fecVen) || moment().endOf("month").toDate();

        const activePeriod = {
          id: "current",
          label: `Período Activo Actual (${moment(ultPago).format(
            "DD/MM/YYYY",
          )} al ${moment(vencimiento).format("DD/MM/YYYY")})`,
          start: ultPago,
          end: vencimiento,
        };

        // Build historical periods from payments subcollection
        const historicalPeriods = [];
        pagosSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.paymentDate) {
            const start = new Date(data.paymentDate + "T00:00:00");
            const end = new Date(start);
            end.setMonth(end.getMonth() + 1);
            end.setSeconds(end.getSeconds() - 1);

            historicalPeriods.push({
              id: doc.id,
              label: `Plan ${
                data.plan || data.description || "Membresía"
              } - Pago: ${moment(start).format("DD/MM/YYYY")} (${moment(
                start,
              ).format("DD/MM/YYYY")} al ${moment(end).format("DD/MM/YYYY")})`,
              start,
              end,
            });
          }
        });

        // Sort historical descending by start date
        historicalPeriods.sort((a, b) => b.start - a.start);

        const allPeriods = [activePeriod, ...historicalPeriods];
        setPeriods(allPeriods);

        // Perform initial query
        await fetchReport(
          activePeriod.start,
          activePeriod.end,
          planesList || [],
        );
      } catch (err) {
        console.error("Error loading WhatsApp report data:", err);
      } finally {
        setLoading(false);
      }
    }
    loadInitialData();
  }, [empresa]);

  const fetchReport = async (startDate, endDate, planesList = planes) => {
    setQueryLoading(true);
    setCurrentPage(1);
    try {
      // Formatear fechas a medianoche
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);

      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      // Query all messages in range
      const querySnapshot = await db
        .collection("empresas")
        .doc(empresa.id)
        .collection("mensajes")
        .where("date", ">=", start)
        .where("date", "<=", end)
        .get();

      const outboundMessages = [];
      const types = {
        text: 0,
        template: 0,
        document: 0,
        image: 0,
        audio: 0,
        video: 0,
      };
      const templatesMap = {};

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Filtrar en cliente los outbound (mensajes salientes)
        if (
          data.direction === "outbound" ||
          data.from === empresa.telefono ||
          (data.to &&
            data.to !== empresa.telefono &&
            data.direction !== "inbound" &&
            data.direction !== "incoming")
        ) {
          const msgDate = convertTimestamp(data.date) || new Date();
          const mapped = {
            id: doc.id,
            date: msgDate,
            to: data.to || "N/A",
            type: data.type || "text",
            message: data.message || data.body || "",
            templateName:
              data.template?.name ||
              (data.type === "template" ? data.template || "General" : null),
          };
          outboundMessages.push(mapped);

          // Contabilizar tipo
          const mType = mapped.type.toLowerCase();
          if (types[mType] !== undefined) {
            types[mType]++;
          } else {
            types.text++; // default fallbacks
          }

          // Contabilizar plantilla
          if (mapped.type === "template" && mapped.templateName) {
            templatesMap[mapped.templateName] =
              (templatesMap[mapped.templateName] || 0) + 1;
          }
        }
      });

      // Ordenar por fecha descendente (más nuevos primero)
      outboundMessages.sort((a, b) => b.date - a.date);
      setMessages(outboundMessages);
      setTypeStats(types);

      // Extraer días únicos con mensajes para el filtro
      const uniqueDays = new Set();
      outboundMessages.forEach((msg) => {
        uniqueDays.add(moment(msg.date).format("YYYY-MM-DD"));
      });
      const sortedDays = Array.from(uniqueDays).sort((a, b) => new Date(b) - new Date(a));
      setAvailableDays(sortedDays);

      // Desglose de plantillas en array ordenado
      const tempArray = Object.keys(templatesMap).map((name) => ({
        name,
        count: templatesMap[name],
      }));
      tempArray.sort((a, b) => b.count - a.count);
      setTemplateStats(tempArray);

      // Calcular límites del plan actual
      const planLimits = getPlanLimits(empresa, planesList);
      const totalOutbound = outboundMessages.length;
      const planLimit = planLimits.maxMensajesWhatsapp;
      const usagePercent =
        planLimit > 0
          ? Math.min(100, Math.round((totalOutbound / planLimit) * 100))
          : 0;

      setSummary({
        totalOutbound,
        planLimit,
        usagePercent,
      });

      // Resetear filtros
      setShowDetails(false);
      setFilterTemplate("all");
      setFilterDay("all");
    } catch (err) {
      console.error("Error fetching messages range:", err);
    } finally {
      setQueryLoading(false);
    }
  };

  const handleQueryClick = () => {
    if (selectedPeriod === "custom") {
      const start = new Date(fecIni + "T00:00:00");
      const end = new Date(fecFin + "T23:59:59");
      fetchReport(start, end);
    } else {
      const period = periods.find((p) => p.id === selectedPeriod);
      if (period) {
        fetchReport(period.start, period.end);
      }
    }
  };

  // Filtrado de mensajes en cliente
  const filteredMessages = messages.filter((msg) => {
    const matchTemplate = filterTemplate === "all" || msg.templateName === filterTemplate;
    const matchDay = filterDay === "all" || moment(msg.date).format("YYYY-MM-DD") === filterDay;
    return matchTemplate && matchDay;
  });

  // Pagination helper
  const totalPages = Math.ceil(filteredMessages.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentMessages = filteredMessages.slice(indexOfFirstItem, indexOfLastItem);

  const getFormatDate = (date) => {
    return moment(date).format("DD/MM/YYYY hh:mm A");
  };

  if (loading) {
    return (
      <MainContainer
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: "60vh" }}
      >
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Cargando períodos y estadísticas...</p>
        </div>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <ContainerTitle>
        <ContainerTitleText>Reporte de Consumo WhatsApp</ContainerTitleText>
      </ContainerTitle>

      <ContainerBody>
        <FlexContainer>
          <label>
            Período Facturado
            <StyledSelect
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
            >
              {periods.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
              <option value="custom">Rango Personalizado...</option>
            </StyledSelect>
          </label>

          {selectedPeriod === "custom" && (
            <>
              <label>
                Fecha Inicio
                <StyledInput
                  type="date"
                  value={fecIni}
                  onChange={(e) => setFecIni(e.target.value)}
                />
              </label>
              <label>
                Fecha Fin
                <StyledInput
                  type="date"
                  value={fecFin}
                  onChange={(e) => setFecFin(e.target.value)}
                />
              </label>
            </>
          )}

          <StyledButton onClick={handleQueryClick} disabled={queryLoading}>
            {queryLoading ? "Consultando..." : "Consultar"}
          </StyledButton>
        </FlexContainer>
      </ContainerBody>

      {queryLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Buscando mensajes enviados...</p>
        </div>
      ) : (
        <>
          {/* 1. Tarjetas de Resumen */}
          <GridResumen>
            <CardResumen>
              <div className="card-title">Total Enviados</div>
              <div className="card-value">{summary.totalOutbound}</div>
              <div className="card-desc">Mensajes salientes registrados</div>
            </CardResumen>
            <CardResumen>
              <div className="card-title">Límite de Plan</div>
              <div className="card-value">
                {summary.planLimit > 0 ? summary.planLimit : "∞"}
              </div>
              <div className="card-desc">Límite mensual del plan</div>
            </CardResumen>
            <CardResumen>
              <div className="card-title">Uso de WhatsApp</div>
              <div className="card-value">{summary.usagePercent}%</div>
              <div className="card-desc">Porcentaje del plan consumido</div>
            </CardResumen>
            <CardResumen>
              <div className="card-title">Plantillas Usadas</div>
              <div className="card-value">{templateStats.length}</div>
              <div className="card-desc">
                Diferentes plantillas Meta enviadas
              </div>
            </CardResumen>
          </GridResumen>

          {/* 2. Sección de Gráficos / Progreso y Desglose de Plantillas */}
          <FlexGridSection>
            {/* Clasificación de Plantillas Meta */}
            <ContainerBody>
              <SectionTitle>Plantillas Meta Utilizadas</SectionTitle>
              {templateStats.length > 0 ? (
                <TemplateList>
                  {templateStats.map((item, idx) => (
                    <TemplateItem key={idx}>
                      <span className="temp-name">{item.name}</span>
                      <span className="temp-count">{item.count} envíos</span>
                    </TemplateItem>
                  ))}
                </TemplateList>
              ) : (
                <div
                  className="text-center py-5 text-muted"
                  style={{ fontSize: "0.9rem" }}
                >
                  No se registraron envíos de plantillas pre-aprobadas en este
                  período.
                </div>
              )}
            </ContainerBody>
          </FlexGridSection>

          {/* 3. Detalle Historial Mensajes */}
          {!showDetails ? (
            <div style={{ textAlign: "center", margin: "24px 0" }}>
              <ToggleButton onClick={() => setShowDetails(true)}>
                🔍 Mostrar Historial Detallado de Mensajes
              </ToggleButton>
            </div>
          ) : (
            <ContainerBody style={{ overflowX: "auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <SectionTitle style={{ margin: 0, border: "none", padding: 0 }}>
                  Historial Detallado de Mensajes Enviados
                </SectionTitle>
                <button 
                  onClick={() => setShowDetails(false)}
                  style={{ 
                    background: "none", 
                    border: "none", 
                    color: "#e64980", 
                    fontSize: "0.85rem", 
                    fontWeight: "600",
                    cursor: "pointer"
                  }}
                >
                  Ocultar Detalle
                </button>
              </div>

              {/* Filtros de detalles */}
              <DetailFiltersContainer>
                <label>
                  Filtrar por Plantilla (Template)
                  <StyledSelect
                    value={filterTemplate}
                    onChange={(e) => {
                      setFilterTemplate(e.target.value);
                      setCurrentPage(1);
                    }}
                    style={{ width: "100%" }}
                  >
                    <option value="all">Todas las plantillas</option>
                    {templateStats.map((t) => (
                      <option key={t.name} value={t.name}>
                        {t.name}
                      </option>
                    ))}
                  </StyledSelect>
                </label>

                <label>
                  Filtrar por Día del Rango
                  <StyledSelect
                    value={filterDay}
                    onChange={(e) => {
                      setFilterDay(e.target.value);
                      setCurrentPage(1);
                    }}
                    style={{ width: "100%" }}
                  >
                    <option value="all">Todos los días</option>
                    {availableDays.map((d) => (
                      <option key={d} value={d}>
                        {moment(d).format("DD/MM/YYYY")}
                      </option>
                    ))}
                  </StyledSelect>
                </label>
              </DetailFiltersContainer>

              {filteredMessages.length > 0 ? (
                <>
                  <Tabla>
                    <TablaHeader>
                      <tr>
                        <TablaHeaderTexto>Fecha y Hora</TablaHeaderTexto>
                        <TablaHeaderTexto>Destinatario</TablaHeaderTexto>
                        <TablaHeaderTexto>Tipo</TablaHeaderTexto>
                        <TablaHeaderTexto>Mensaje / Contenido</TablaHeaderTexto>
                      </tr>
                    </TablaHeader>
                    <TablaBody>
                      {currentMessages.map((msg) => (
                        <TablaBodyRow key={msg.id}>
                          <TablaBodyTexto>
                            {getFormatDate(msg.date)}
                          </TablaBodyTexto>
                          <TablaBodyTexto>+{msg.to}</TablaBodyTexto>
                          <TablaBodyTexto>
                            <span className={`badge-type type-${msg.type}`}>
                              {msg.type}
                            </span>
                          </TablaBodyTexto>
                          <TablaBodyTexto className="preview-cell">
                            {msg.type === "template" ? (
                              <span>
                                <strong>Plantilla:</strong>{" "}
                                <code style={{ color: "#0c8de3" }}>
                                  {msg.templateName}
                                </code>
                                {msg.message && (
                                  <div
                                    className="text-muted mt-1"
                                    style={{ fontSize: "0.8rem" }}
                                  >
                                    {msg.message}
                                  </div>
                                )}
                              </span>
                            ) : (
                              msg.message || (
                                <span className="text-muted italic">
                                  (Archivo adjunto)
                                </span>
                              )
                            )}
                          </TablaBodyTexto>
                        </TablaBodyRow>
                      ))}
                    </TablaBody>
                  </Tabla>

                  <PaginatorContainer>
                    <div>
                      Mostrando del {indexOfFirstItem + 1} al{" "}
                      {Math.min(indexOfLastItem, filteredMessages.length)} de{" "}
                      {filteredMessages.length} mensajes.
                    </div>
                    <div className="btn-group">
                      <button
                        onClick={() =>
                          setCurrentPage((prev) => Math.max(1, prev - 1))
                        }
                        disabled={currentPage === 1}
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() =>
                          setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                        }
                        disabled={currentPage === totalPages}
                      >
                        Siguiente
                      </button>
                    </div>
                  </PaginatorContainer>
                </>
              ) : (
                <div className="text-center py-4 text-muted">
                  No se encontraron mensajes salientes con los filtros seleccionados.
                </div>
              )}
            </ContainerBody>
          )}
        </>
      )}
    </MainContainer>
  );
};
